import React from "react";
import PageHeader from "@/components/PageHeader";
import { COMPANY, CAPABILITIES } from "@/data/site";
import { Building2, Factory, Truck, Users } from "lucide-react";
import Seo from "@/components/Seo";

const VALUES = [
  {
    icon: Factory,
    title: "Manufacturing First",
    body: "We build, not just resell. Cell assembly, BMS, pole fabrication and EV assembling — all under one roof in Hyderabad.",
  },
  {
    icon: Building2,
    title: "B2B Discipline",
    body: "Documentation, GST-compliant billing, structured QC reports and AMC support tailored for industrial buyers.",
  },
  {
    icon: Truck,
    title: "Pan-India Delivery",
    body: "Logistics partners and field engineers across India for installation, commissioning and on-site servicing.",
  },
  {
    icon: Users,
    title: "Community Roots",
    body: "Dedicated CSR & student-project arm to extend clean-energy access to schools, villages and emerging engineers.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        path="/about"
        title="About"
        description="Hyderabad-based manufacturer of lithium-ion batteries, solar street lights, electric vehicles and turnkey CSR-led energy projects. End-to-end in-house engineering at our Cherlapally facility."
      />
      <PageHeader
        eyebrow="About VPS"
        title="A Hyderabad-built energy company."
        subtitle="VOLTECH POWER SOLUTIONS designs, manufactures and services lithium-powered solutions — from EV drivetrains and traffic-signal batteries to solar street lights and CSR-driven rural electrification."
        testId="about-page-header"
      />

      <section className="py-20 md:py-28 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow">Who we are</div>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 tracking-tighter leading-[1.05]">
              Lithium, sunlight and steel — built for India.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-zinc-300 leading-relaxed text-[15px]">
            <p>
              VOLTECH POWER SOLUTIONS (VPS) operates from BN Reddy Nagar,
              Cherlapally — Hyderabad&apos;s industrial heartland. Our work spans
              the full lithium value chain: raw-material supply, custom pack
              design, BMS engineering, EV assembling and after-sales service.
            </p>
            <p>
              We pair this with a solar lighting practice that handles
              everything from in-house pole fabrication to last-mile
              installation, and an active CSR programme that brings clean
              energy to schools, public spaces and rural communities across
              Telangana and beyond.
            </p>
            <p>
              We work directly with OEMs, e-rickshaw operators, municipalities,
              public-sector buyers and large CSR sponsors. Every solution is
              traceable to a part-number, every install to a service log.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-white/10 bg-[#0a0a0c]" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="eyebrow">Operating principles</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
            How we work.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="industrial-card p-6" data-testid={`about-value-${i}`}>
                  <Icon size={22} className="text-[#e63946]" />
                  <div className="font-display text-lg uppercase mt-4 leading-tight">
                    {v.title}
                  </div>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="eyebrow">Capabilities</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
            What we can build for you.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c) => (
              <div key={c.code} className="industrial-card p-6" data-testid={`about-capability-${c.code}`}>
                <div className="font-mono text-xs text-[#e63946] tracking-[0.2em]">
                  {c.code}
                </div>
                <div className="font-display text-lg uppercase mt-3 leading-tight">
                  {c.title}
                </div>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 industrial-card p-8 md:p-10" data-testid="about-corporate-block">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="eyebrow">Corporate</div>
                <div className="font-display text-xl uppercase mt-3">
                  {COMPANY.name}
                </div>
                <div className="text-sm text-zinc-400 mt-2 font-mono">
                  GSTIN · {COMPANY.gstin}
                </div>
              </div>
              <div>
                <div className="eyebrow">Address</div>
                <div className="text-sm text-zinc-300 mt-3 leading-relaxed">
                  {COMPANY.address}
                </div>
              </div>
              <div>
                <div className="eyebrow">Reach us</div>
                <div className="text-sm text-zinc-300 mt-3 space-y-1">
                  <div>Sales · {COMPANY.phoneSales}</div>
                  <div>Service · {COMPANY.phoneService}</div>
                  <div>{COMPANY.email}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
